/**
 * APR-3556 — lista do seletor de cidade (`/`, select-city), extraída pura para
 * ser testável sem Angular.
 *
 * A fonte passa a ser a API (GET cities/public/list: cidades ativas), que já
 * inclui as cidades criadas pelo wizard do Backoffice. O `citiesWhitelist`
 * fixo continua servindo para duas coisas:
 *  - reserva: API falhou ou veio vazia => a tela mostra a lista fixa, como antes;
 *  - `loginUrl`: cidade que loga num sistema externo (hoje só Blumenau) não tem
 *    esse endereço no cadastro da cidade; ele segue vindo da lista fixa.
 *  - nome: cidade que já está na lista fixa mantém o nome de lá; só as que não
 *    estão (as do wizard) usam o `title` do cadastro.
 */

import { PublicCityListItem } from '../../domain/city/dtos/get-public-city-list.output';
import { WhitelistEntry } from './tenant-resolution';

export interface SelectCityOption {
  name: string;
  identity: string;
  loginUrl?: string;
}

export function buildSelectCityOptions(
  apiCities: PublicCityListItem[] | null | undefined,
  whitelist: WhitelistEntry[]
): SelectCityOption[] {
  if (!apiCities?.length) {
    return whitelist.map(({ name, identity, loginUrl }) => ({ name, identity, ...(loginUrl ? { loginUrl } : {}) }));
  }

  const loginUrlByIdentity = new Map(
    whitelist.filter((entry) => entry.loginUrl).map((entry) => [entry.identity, entry.loginUrl as string])
  );
  const nameByIdentity = new Map(whitelist.map((entry) => [entry.identity, entry.name]));

  const options: SelectCityOption[] = apiCities
    .filter((city) => city?.identidade)
    .map((city) => {
      const loginUrl = loginUrlByIdentity.get(city.identidade);
      return {
        name: nameByIdentity.get(city.identidade) || city.title || city.identidade,
        identity: city.identidade,
        ...(loginUrl ? { loginUrl } : {}),
      };
    });

  // Cidade de login externo fica no seletor mesmo que a API não a devolva:
  // o acesso é no sistema da prefeitura, não depende do `active` da Aprova.
  const returned = new Set(options.map((option) => option.identity));
  for (const entry of whitelist) {
    if (entry.loginUrl && !returned.has(entry.identity)) {
      options.push({ name: entry.name, identity: entry.identity, loginUrl: entry.loginUrl });
    }
  }

  return options;
}
